import { Button, Form, Input, Result } from "antd";
import validateMessages from "../../utils/validate";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import ROUTES from "../../routes";
import { useResetPasswordMutation, useSendOtpMutation, useVerifyOtpMutation } from "../../services/auth";
import hrnLocalStorage from "../../utils/hrnLocalStorage";
import Password from "antd/es/input/Password";

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [fpwData, setFpwData] = useState<any>(hrnLocalStorage.getForgotPasswordData() || { step: 0 })
    const [sendOtpForm] = Form.useForm();
    const [verifyOtpForm] = Form.useForm();
    const [resetPassForm] = Form.useForm();

    const [sendOtp, sendOtpResult] = useSendOtpMutation();
    const [verifyOtp, verifyOtpResult] = useVerifyOtpMutation();
    const [resetPassword, resetPasswordResult] = useResetPasswordMutation();

    const password = Form.useWatch("password", resetPassForm);
    const validateConfirmPassword = (rule: any, value: string) => {
        if (value && password) {
            if (value === password) {
                return Promise.resolve();
            }
            return Promise.reject(new Error("Password doesn't match"));
        }
        return Promise.resolve();
    };

    const onBack = (step: string) => {
        if (step == "-1") {
            navigate(ROUTES.PATHS.LOGIN);
        }
        else {
            const newFpwData = { ...fpwData, step };
            setFpwData(newFpwData);
            hrnLocalStorage.setForgotPasswordData(newFpwData);
        }
    };
    const handleSendOtp = async () => {
        try {
            await sendOtpForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = sendOtpForm.getFieldsValue();
        const payload = {
            identityType: "Email",
            identityNumber: formFields.email || fpwData.email
        }
        try {
            await sendOtp(payload).unwrap();
            const newFpwData = { ...fpwData, email: payload.identityNumber, step: "1" };
            setFpwData(newFpwData);
            hrnLocalStorage.setForgotPasswordData(newFpwData);
            verifyOtpForm.setFieldValue("email", payload.identityNumber);
        } catch (e) { }
    };

    const handleVerifyOtp = async () => {
        try {
            await verifyOtpForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = verifyOtpForm.getFieldsValue();
        const payload = {
            identityType: "Email",
            identityNumber: formFields.email || fpwData.email,
            otp: formFields.otp
        }
        try {
            await verifyOtp(payload).unwrap();
            if (verifyOtpResult.isSuccess) {
                const newFpwData = {
                    ...fpwData,
                    email: payload.identityNumber,
                    userId: verifyOtpResult.data.data.userId,
                    step: "2"
                };
                setFpwData(newFpwData);
                hrnLocalStorage.setForgotPasswordData(newFpwData);
            }
        } catch (e) { }
    };

    useEffect(() => {
        if (verifyOtpResult.isSuccess && verifyOtpResult?.data?.data) {
            const newFpwData = { ...fpwData, step: "2", userId: verifyOtpResult.data.data.userId };
            setFpwData(newFpwData);
            hrnLocalStorage.setForgotPasswordData(newFpwData);
        }
    }, [verifyOtpResult.isLoading]);

    const handleReset = async () => {
        const fpwData = hrnLocalStorage.getForgotPasswordData();
        try {
            await resetPassForm.validateFields();
        } catch (errorInfo) {
            return;
        }
        const formFields = resetPassForm.getFieldsValue();
        const payload = {
            userId: fpwData.userId,
            password: formFields.password
        }
        try {
            await resetPassword(payload).unwrap();
            const newFpwData = { ...fpwData, step: "3" };
            setFpwData(newFpwData);
            hrnLocalStorage.setForgotPasswordData(newFpwData);
        } catch (e) { }
    };

    const goToLogin = () => {
        hrnLocalStorage.clearForgotPasswordData();
        navigate(ROUTES.PATHS.LOGIN);
    };

    return (
        <div className="w-[80vw]" style={{ maxWidth: "500px", margin: "50px auto" }}>
            {fpwData.step == "0" && <Form
                form={sendOtpForm}
                layout="vertical"
                name="sendOtpForm"
                validateMessages={validateMessages}
            >
                <div>
                    <h2 className="mt-0 text-primary"><Icon
                        icon="arrow_back"
                        className="h-5 w-5 cursor-pointer mr-1"
                        onClick={() => onBack("-1")}
                    />
                        Forgot Password
                    </h2>
                </div>
                <div className="mb-5">
                    <p>
                        Please select your email for receiving password recovery code.
                    </p>
                    <Form.Item name="email" label="Your login email" rules={[{ required: true, type: "email" }]} >
                        <Input inputMode="email" size="large" placeholder="Email address" prefix={<MailOutlined size={36} className="pr-1.5 text-black opacity-60" />} />
                    </Form.Item>
                    <Button onClick={handleSendOtp} type="primary" block size="large">
                        {sendOtpResult.isLoading ? "Please wait..." : "Send OTP"}
                    </Button>
                </div>
            </Form>}
            {fpwData.step == "1" && <Form
                form={verifyOtpForm}
                layout="vertical"
                name="verifyOtpForm"
                validateMessages={validateMessages}
            >
                <div className="mb-5">
                    <div>
                        <h2 className="mt-0 text-primary"><Icon
                            icon="arrow_back"
                            className="h-5 w-5 cursor-pointer mr-1"
                            onClick={() => onBack("0")}
                        />
                            Verify OTP
                        </h2>
                    </div>
                    <p>
                        We have sent a 6 digit code to your email address. It will expire in 2 minutes.
                    </p>
                    <Form.Item name="email" label="Your login email" rules={[{ type: "email" }]} >
                        <Input inputMode="email" disabled defaultValue={fpwData.email} size="large" placeholder="Email address" prefix={<MailOutlined size={36} className="pr-1.5 text-black opacity-60" />} />
                    </Form.Item>
                    <Form.Item name="otp" label="OTP" rules={[{ required: true }]} style={{ marginBottom: "0.25rem" }} >
                        <Input.OTP size="large" />
                    </Form.Item>
                    <div className="text-right">
                        <Button disabled={sendOtpResult.isLoading} onClick={handleSendOtp} type="link">Resend OTP</Button>
                    </div>
                    <Button onClick={handleVerifyOtp} type="primary" block size="large">
                        {verifyOtpResult.isLoading ? "Please wait..." : "Verify OTP"}
                    </Button>
                </div>
            </Form>}
            {fpwData.step == "2" && <Form
                form={resetPassForm}
                layout="vertical"
                name="resetPassForm"
                validateMessages={validateMessages}
            >
                <div className="mb-5">
                    <div>
                        <h2 className="mt-0 text-primary"><Icon
                            icon="arrow_back"
                            className="h-5 w-5 cursor-pointer mr-1"
                            onClick={() => onBack("1")}
                        />
                            Reset Password
                        </h2>
                    </div>
                    <p>
                        Choose new password
                    </p>
                    <Form.Item name="password" label="Password" rules={[{ required: true }]}>
                        <Password size="large" placeholder="Password" prefix={<LockOutlined size={36} className="pr-1.5 text-black opacity-60" />} />
                    </Form.Item>
                    <Form.Item name="confirmPassword" label="Confirm password" rules={[{ required: true }, { validator: validateConfirmPassword }]}>
                        <Password size="large" placeholder="Confirm password" prefix={<LockOutlined size={36} className="pr-1.5 text-black opacity-60" />} />
                    </Form.Item>
                    <Button onClick={handleReset} type="primary" block size="large">
                        {resetPasswordResult.isLoading ? "Please wait..." : "Reset Password"}
                    </Button>
                </div>
            </Form>}
            {fpwData.step == "3" &&
                <div className="mb-5 w-[100%]">
                    <Result
                        status="success"
                        title="Successfully reset your password"
                        subTitle="Please login to confirm"
                        extra={[<Button type="link" key="login" onClick={goToLogin} > Go to login</Button>]}
                    />
                </div>
            }
        </div>
    );
};

export default ForgotPassword;
